import React from "react";
import {
    Layout,
    Card,
    Typography,
    Divider,
    Tag,
    Button,
    Row,
    Col,
    Tooltip,
} from "antd";
import { LeftOutlined, WarningOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";

const { Content } = Layout;
const { Title, Text } = Typography;

const MarketDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const data = location.state?.marketplaceData;

    if (!data) {
        return (
            <div style={{ padding: 40, textAlign: "center" }}>
                No product data found
            </div>
        );
    }

    return (
        <div style={{ padding: 24, background: "#f5f7fb", minHeight: "100vh" }}>
            {/* 🔙 Back */}
            <Button
                icon={<LeftOutlined />}
                type="link"
                onClick={() => navigate(-1)}
                style={{ marginBottom: 16 }}
            >
                Back
            </Button>

            <Layout>
                <Content>
                    {/* 🔥 HERO */}
                    <div
                        style={{
                            position: "relative",
                            borderRadius: 16,
                            overflow: "hidden",
                            boxShadow: "0 6px 25px rgba(0,0,0,0.1)",
                        }}
                    >
                        <img
                            src={data.image}
                            alt="product"
                            style={{
                                width: "100%",
                                height: 240,
                                objectFit: "cover",
                            }}
                        />
                        {data.isReported && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: 16,
                                    right: 16,
                                    background: "rgba(255, 77, 79, 0.95)",
                                    color: "#fff",
                                    padding: "6px 14px",
                                    borderRadius: 20,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    fontWeight: 500,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                }}
                            >
                                <WarningOutlined />
                                Reported Item
                            </div>
                        )}

                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                background: "rgba(0,0,0,0.45)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-end",
                                padding: 20,
                            }}
                        >
                            <Title level={3} style={{ color: "#fff", margin: 0 }}>
                                {data.name}
                            </Title>

                            <div style={{ marginTop: 8, display: "flex", gap: 10 }}>
                                <Tag color="gold">₹ {data.price}</Tag>
                                <Tag color="blue">{data.condition}</Tag>
                                <Tag color="purple">{data.location}</Tag>
                            </div>
                        </div>
                    </div>

                    {/* 🔥 DESCRIPTION */}
                    {/* 🔥 COMBINED DETAILS CARD */}
                    <div style={{ marginTop: 24 }}>
                        <Card style={{ borderRadius: 16 }}>
                            {/* DESCRIPTION */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div>
                                    <Title level={4}>Description</Title>
                                    <Text type="secondary">
                                        {data.description || "No description available"}
                                    </Text>
                                </div>
                                <Tooltip title='Item status'>
                                    <div
                                    >
                                        {data.isApproved === "1" && (
                                            <Tag color="green"
                                                style={{
                                                    fontSize: "14px",
                                                    padding: "4px 12px",
                                                    lineHeight: "24px",
                                                    borderRadius: "6px",
                                                }}>Approved</Tag>
                                        )}

                                        {data.isApproved === "2" && (
                                            <Tag color="red" style={{
                                                fontSize: "14px",
                                                padding: "4px 12px",
                                                lineHeight: "24px",
                                                borderRadius: "6px",
                                            }}>Rejected</Tag>
                                        )}

                                        {(data.isApproved === "0" ||
                                            !data.isApproved) && (
                                                <Tag color="orange" style={{
                                                    fontSize: "14px",
                                                    padding: "4px 12px",
                                                    lineHeight: "24px",
                                                    borderRadius: "6px",
                                                }}>Pending</Tag>
                                            )}
                                    </div>
                                </Tooltip>
                            </div>

                            <Divider />

                            {/* DETAILS */}
                            <Title level={4}>Product Details</Title>

                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Card style={{ borderRadius: 12 }}>
                                        <Text strong>Product Name</Text>
                                        <div style={{ marginTop: 6 }}>
                                            {data.name}
                                        </div>
                                    </Card>
                                </Col>

                                <Col span={12}>
                                    <Card style={{ borderRadius: 12 }}>
                                        <Text strong>Price</Text>
                                        <div style={{ marginTop: 6 }}>
                                            ₹ {data.price}
                                        </div>
                                    </Card>
                                </Col>
                                {data.rejectedReason &&
                                    <Col span={12}>
                                        <Card style={{ borderRadius: 12 }}>
                                            <Text strong>Reason for Rejection</Text>
                                            <div style={{ marginTop: 6 }}>
                                                {data.rejectedReason}
                                            </div>
                                        </Card>
                                    </Col>

                                }

                                <Col span={12}>
                                    <Card style={{ borderRadius: 12 }}>
                                        <Text strong>Condition</Text>
                                        <div style={{ marginTop: 6 }}>
                                            <Tag color="blue">{data.condition}</Tag>
                                        </div>
                                    </Card>
                                </Col>

                                <Col span={12}>
                                    <Card style={{ borderRadius: 12 }}>
                                        <Text strong>Location</Text>
                                        <div style={{ marginTop: 6 }}>
                                            {data.location}
                                        </div>
                                    </Card>
                                </Col>

                                <Col span={12}>
                                    <Card style={{ borderRadius: 12 }}>
                                        <Text strong>Contact Email</Text>
                                        <div style={{ marginTop: 6 }}>
                                            {data.email || "N/A"}
                                        </div>
                                    </Card>
                                </Col>

                                <Col span={12}>
                                    <Card style={{ borderRadius: 12 }}>
                                        <Text strong>Contact Mobile</Text>
                                        <div style={{ marginTop: 6 }}>
                                            {data.mobile || "N/A"}
                                        </div>
                                    </Card>
                                </Col>

                                <Col span={12}>
                                    <Card style={{ borderRadius: 12 }}>
                                        <Text strong>Created Date</Text>
                                        <div style={{ marginTop: 6 }}>
                                            {data.createdDate || "Auto Generated"}
                                        </div>
                                    </Card>
                                </Col>

                                <Col span={12}>
                                    <Card style={{ borderRadius: 12 }}>
                                        <Text strong>Product ID</Text>
                                        <div style={{ marginTop: 6 }}>
                                            {data.id}
                                        </div>
                                    </Card>
                                </Col>
                            </Row>
                        </Card>
                    </div>
                </Content>
            </Layout>
        </div>
    );
};

export default MarketDetails;